import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useCart, useCurrency, API } from "../../App";
import UserLayout from "../../components/UserLayout";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft, Plus, Minus, Trash2, MapPin, Loader2, AlertCircle, Sparkles, User, Phone, Mail, FileText, Store, Truck, Wallet, CreditCard, BadgePercent, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export default function UserCart() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { cart, addToCart, updateQuantity, removeFromCart, getTotal, clearCart } = useCart();
  const { symbol } = useCurrency();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [forcedAddress, setForcedAddress] = useState(null);
  const [orderNotes, setOrderNotes] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [orderType, setOrderType] = useState(() => sessionStorage.getItem("sm_pickup_mode") === "true" ? "pickup" : "delivery");
  const [kitchenInfo, setKitchenInfo] = useState(null);
  const [hasBillingAddress, setHasBillingAddress] = useState(true);
  const [billingMatchesShipping, setBillingMatchesShipping] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("wallet"); // "wallet" or "gateway"
  const [discountConfig, setDiscountConfig] = useState(null);
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [totalsPreview, setTotalsPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [promoBanner, setPromoBanner] = useState(null);
  const [billExpanded, setBillExpanded] = useState(false);

  const isPickupMode = sessionStorage.getItem("sm_pickup_mode") === "true";

  useEffect(() => {
    fetchAddresses();
    fetchBillingAddress();
    fetchDiscountConfig();
    // Cart promo banner is fully admin-editable via /admin/settings; fetch once
    // on mount. Silently no-ops on network error — the section just stays hidden.
    fetch(`${API}/settings/cart-promo-banner`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setPromoBanner(d))
      .catch(() => {});
    if (cart.length > 0) fetchRecommendations();
    const kitchenId = cart[0]?.kitchen_id;
    if (kitchenId) {
      fetchKitchenInfo(kitchenId);
      fetchForcedShipping(kitchenId);
    } else {
      setForcedAddress(null);
    }
  }, [cart.length]);

  // Refresh totals preview whenever cart changes, payment method, or order type switches.
  // We track a monotonically-incrementing request id and ignore any response whose id
  // is older than the latest fired — this prevents an in-flight delivery preview from
  // overwriting a fresh pickup preview when the user toggles modes quickly.
  const previewSeqRef = useRef(0);
  useEffect(() => {
    if (cart.length === 0) { setTotalsPreview(null); return; }
    const payload = {
      items: cart.map((item) => ({
        item_id: item.item_id, quantity: item.quantity,
        selected_variations: (item.selected_variations || []).map((v) => ({
          variation_id: v.variation_id, option_id: v.option_id,
          variation_name: v.variation_name, option_value: v.option_value,
          price_adjustment: v.price_adjustment,
        })),
        selected_addons: (item.selected_addons || []).map((a) => ({
          addon_id: a.addon_id, name: a.name, price: a.price, group_name: a.group_name,
        })),
      })),
      payment_method: paymentMethod,
      order_type: orderType,
    };
    setPreviewLoading(true);
    const mySeq = ++previewSeqRef.current;
    xhrPost(`${API}/orders/preview-totals`, payload)
      .then(({ ok, data }) => {
        // Only apply this response if it's still the latest in-flight request.
        if (mySeq !== previewSeqRef.current) return;
        if (ok) setTotalsPreview(data);
      })
      .catch(() => {})
      .finally(() => {
        if (mySeq === previewSeqRef.current) setPreviewLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, paymentMethod, orderType]);

  const fetchDiscountConfig = async () => {
    try {
      const res = await fetch(`${API}/settings/discount`);
      if (res.ok) setDiscountConfig(await res.json());
    } catch {}
  };

  const fetchKitchenInfo = async (kitchenId) => {
    try {
      const res = await fetch(`${API}/user/kitchen/${kitchenId}`, { credentials: "include" });
      if (res.ok) {
        const k = await res.json();
        setKitchenInfo(k);
      }    } catch (e) { console.error(e); }
  };

  const fetchForcedShipping = async (kitchenId) => {
    try {
      const r = await fetch(`${API}/orders/forced-shipping?kitchen_id=${kitchenId}`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setForcedAddress(data.forced ? data : null);
      }
    } catch {}
  };

  const fetchBillingAddress = async () => {
    try {
      const res = await fetch(`${API}/billing-address`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setHasBillingAddress(data && data.address_line ? true : false);
      } else {
        setHasBillingAddress(false);
      }
    } catch (e) { setHasBillingAddress(false); }
  };

  const fetchAddresses = async () => {
    try {
      const res = await fetch(`${API}/addresses`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAddresses(data);
        // Use the address selected during menu browsing (locked)
        const menuAddressId = localStorage.getItem("supermorsel_checkout_address");
        if (menuAddressId) {
          const menuAddr = data.find((a) => a.address_id === menuAddressId);
          if (menuAddr) {
            setSelectedAddressId(menuAddr.address_id);
            return;
          }
        }
        const defaultAddr = data.find((a) => a.is_default) || data[0];
        if (defaultAddr && !selectedAddressId) setSelectedAddressId(defaultAddr.address_id);
      }
    } catch (e) { console.error(e); }
  };

  const fetchRecommendations = async () => {
    try {
      const kitchenId = cart[0]?.kitchen_id;
      if (!kitchenId) return;
      // Only fetch items from the selected kitchen
      const response = await fetch(`${API}/user/kitchen-menu/${kitchenId}`, { credentials: "include" });
      if (response.ok) {
        const kitchenItems = await response.json();
        const cartItemIds = new Set(cart.map((i) => i.item_id));
        setRecommendations(kitchenItems.filter((item) => !cartItemIds.has(item.item_id)).sort((a, b) => (b.is_bestseller ? 1 : 0) - (a.is_bestseller ? 1 : 0)).slice(0, 6));
      }
    } catch (error) { console.error(error); }
  };

  // XHR-based POST to bypass Capacitor's native HTTP interception
  const xhrPost = (url, body) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.withCredentials = true;
      xhr.onload = () => {
        let data;
        try { data = JSON.parse(xhr.responseText); } catch { data = { detail: xhr.responseText || "Server error" }; }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(JSON.stringify(body));
    });
  };

  const getItemPrice = (item) => {
    let price = item.price || 0;
    (item.selected_variations || []).forEach((v) => { price += v.price_adjustment || 0; });
    (item.selected_addons || []).forEach((a) => { price += a.price || 0; });
    return price;
  };

  // Prefer authoritative server-calculated totals; fall back to local math until preview loads
  const localSubtotal = Math.round(getTotal());
  const localDiscount = (() => {
    if (!discountConfig || !discountConfig.enabled) return 0;
    const { applicable_to, type, value } = discountConfig;
    const applies = applicable_to === "both" || applicable_to === paymentMethod;
    if (!applies) return 0;
    if (type === "percentage") return Math.round(localSubtotal * (value / 100));
    return Math.min(Math.round(value), localSubtotal);
  })();

  const subtotal = totalsPreview ? totalsPreview.subtotal : localSubtotal;
  const discountAmount = totalsPreview ? totalsPreview.discount_amount : localDiscount;
  const taxAmount = totalsPreview ? totalsPreview.total_tax : 0;
  const taxBreakdown = (totalsPreview && totalsPreview.tax_breakdown) || [];
  const packagingCharge = (totalsPreview && totalsPreview.packaging_charge) || 0;
  const roundingAdjustment = totalsPreview ? totalsPreview.rounding_adjustment : 0;
  const total = totalsPreview ? totalsPreview.total_amount : (localSubtotal - localDiscount);
  const discountLabel = (totalsPreview && totalsPreview.discount_label) || discountConfig?.label || "Discount";
  const minOrderValue = (totalsPreview && totalsPreview.min_order_value) || 0;
  const movSatisfied = totalsPreview ? totalsPreview.min_order_value_satisfied !== false : true;

  const hasInsufficientBalance = paymentMethod === "wallet" && user && Math.round(user.wallet_balance) < total;
  const selectedAddress = addresses.find((a) => a.address_id === selectedAddressId);

  const handlePlaceOrder = async () => {
    if (!user?.phone || !user?.email) {
      toast.error("Please add your phone number and email to your profile first");
      navigate("/profile");
      return;
    }
    const isPickup = orderType === "pickup";
    if (!isPickup && !selectedAddressId) { toast.error("Please select a delivery address"); return; }
    
    if (paymentMethod === "wallet") {
      if (hasInsufficientBalance) {
        toast.error(`Insufficient balance. You need ${symbol}${Number(total - Math.round(user.wallet_balance)).toFixed(2)} more.`);
        navigate("/wallet");
        return;
      }
      await placeOrderWithWallet(isPickup);
    } else {
      // Payment gateway flow: pay first, then place order
      await placeOrderWithGateway(isPickup);
    }
  };

  const placeOrderWithWallet = async (isPickup) => {
    setLoading(true);
    try {
      const addr = isPickup ? null : selectedAddress;
      const addressStr = isPickup ? "" : (addr ? `${addr.address_line}, ${addr.landmark || ''}, ${addr.city}, ${addr.state} ${addr.pincode}`.replace(/, ,/g, ',').trim() : "");
      const { ok, data } = await xhrPost(`${API}/orders`, {
        items: cart.map((item) => ({
          item_id: item.item_id, quantity: item.quantity,
          selected_variations: (item.selected_variations || []).map((v) => ({ variation_id: v.variation_id, option_id: v.option_id, variation_name: v.variation_name, option_value: v.option_value, price_adjustment: v.price_adjustment })),
          selected_addons: (item.selected_addons || []).map((a) => ({ addon_id: a.addon_id, name: a.name, price: a.price, group_name: a.group_name })),
        })),
        delivery_address: addressStr,
        delivery_address_id: isPickup ? null : selectedAddressId,
        delivery_instructions: deliveryInstructions || null, order_notes: orderNotes || null,
        kitchen_id: cart[0]?.kitchen_id || null,
        order_type: orderType,
        payment_method: "wallet",
      });
      if (!ok) throw new Error(data.detail || "Failed to place order");
      clearCart(); sessionStorage.removeItem("sm_pickup_mode"); await refreshUser(); toast.success("Order placed!");
      navigate(`/orders/${data.order_id}`);
    } catch (error) { toast.error(error.message); } finally { setLoading(false); }
  };

  const placeOrderWithGateway = async (isPickup) => {
    setGatewayLoading(true);
    try {
      // Step 1: Create Razorpay order (server-calc based on cart items)
      const cartItemsPayload = cart.map((item) => ({
        item_id: item.item_id, quantity: item.quantity,
        selected_variations: (item.selected_variations || []).map((v) => ({ variation_id: v.variation_id, option_id: v.option_id, variation_name: v.variation_name, option_value: v.option_value, price_adjustment: v.price_adjustment })),
        selected_addons: (item.selected_addons || []).map((a) => ({ addon_id: a.addon_id, name: a.name, price: a.price, group_name: a.group_name })),
      }));
      const { ok: payOk, data: payData } = await xhrPost(`${API}/orders/create-payment`, { items: cartItemsPayload, order_type: orderType });
      if (!payOk) throw new Error(payData.detail || "Payment creation failed");

      // Step 2: Open Razorpay checkout and wait for result
      const razorpayResponse = await openRazorpayForOrder(payData);
      if (!razorpayResponse) { setGatewayLoading(false); return; }

      // Step 3: Verify payment
      const { ok: verifyOk, data: verifyData } = await xhrPost(`${API}/orders/verify-payment`, razorpayResponse);
      if (!verifyOk) throw new Error(verifyData.detail || "Verification failed");

      // Step 4: Place the order
      const addr = isPickup ? null : selectedAddress;
      const addressStr = isPickup ? "" : (addr ? `${addr.address_line}, ${addr.landmark || ''}, ${addr.city}, ${addr.state} ${addr.pincode}`.replace(/, ,/g, ',').trim() : "");
      const { ok: orderOk, data: orderData } = await xhrPost(`${API}/orders`, {
        items: cart.map((item) => ({
          item_id: item.item_id, quantity: item.quantity,
          selected_variations: (item.selected_variations || []).map((v) => ({ variation_id: v.variation_id, option_id: v.option_id, variation_name: v.variation_name, option_value: v.option_value, price_adjustment: v.price_adjustment })),
          selected_addons: (item.selected_addons || []).map((a) => ({ addon_id: a.addon_id, name: a.name, price: a.price, group_name: a.group_name })),
        })),
        delivery_address: addressStr,
        delivery_address_id: isPickup ? null : selectedAddressId,
        delivery_instructions: deliveryInstructions || null, order_notes: orderNotes || null,
        kitchen_id: cart[0]?.kitchen_id || null,
        order_type: orderType,
        payment_method: "gateway",
      });
      if (!orderOk) throw new Error(orderData.detail || "Order placement failed");

      clearCart(); sessionStorage.removeItem("sm_pickup_mode"); await refreshUser(); toast.success("Order placed!");
      navigate(`/orders/${orderData.order_id}`);
    } catch (error) { toast.error(error.message || "Payment failed"); }
    finally { setGatewayLoading(false); }
  };

  const openRazorpayForOrder = (payData) => {
    return new Promise((resolve) => {
      const isWebView = !!(window.Capacitor || window.NativeBridge);

      if (isWebView) {
        const Checkout = window.Capacitor?.Plugins?.Checkout;
        if (Checkout) {
          Checkout.open({
            key: payData.key_id, amount: String(payData.amount), currency: payData.currency,
            name: "Super Morsel", description: "Order Payment", order_id: payData.razorpay_order_id,
            prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
            theme: { color: "#16a34a" },
          }).then((result) => {
            const pd = result?.response || result;
            if (pd?.razorpay_payment_id) {
              resolve({ razorpay_payment_id: pd.razorpay_payment_id, razorpay_order_id: pd.razorpay_order_id || payData.razorpay_order_id, razorpay_signature: pd.razorpay_signature });
            } else { toast.info("Payment cancelled"); resolve(null); }
          }).catch((err) => {
            if (err?.code === 2 || err?.message?.includes("cancelled")) toast.info("Payment cancelled");
            else toast.error(err?.description || "Payment failed");
            resolve(null);
          });
        } else { toast.error("Payment gateway not available"); resolve(null); }
      } else {
        const rzp = new window.Razorpay({
          key: payData.key_id, amount: payData.amount, currency: payData.currency,
          name: "Super Morsel", description: "Order Payment", order_id: payData.razorpay_order_id,
          handler: (response) => { resolve(response); },
          prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
          theme: { color: "#16a34a" },
          modal: { ondismiss: () => { toast.info("Payment cancelled"); resolve(null); } },
        });
        rzp.on("payment.failed", () => { toast.error("Payment failed"); resolve(null); });
        rzp.open();
      }
    });
  };

  if (cart.length === 0) {
    return (
      <UserLayout showHeader={false} showNav={false}>
        <div className="p-4">
          <button className="flex items-center gap-2 mb-6 text-gray-500 text-sm" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
          <div className="empty-state">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Cart is empty</h3>
            <p className="text-sm text-gray-500 mb-4">Add items to get started</p>
            <Button onClick={() => navigate("/menu")} className="bg-green-600 hover:bg-green-700 text-white px-6">Browse Menu</Button>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout showHeader={false} showNav={false}>
      <div className="pb-72 animate-fade-in" data-testid="user-cart">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-gray-800">
          <button onClick={() => navigate(-1)} className="text-gray-500"><ArrowLeft size={18} /></button>
          <h1 className="text-base font-bold text-gray-900 dark:text-white">Checkout</h1>
        </div>

        {/* Missing Profile Info Warning */}
        {(!user?.phone || !user?.email) && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800/30">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-700">Complete Your Profile</p>
                <p className="text-xs text-amber-600">{!user?.phone && !user?.email ? "Phone number and email are" : !user?.phone ? "Phone number is" : "Email is"} required to place orders</p>
                <Button onClick={() => navigate("/profile")} size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="complete-profile-btn">
                  Complete Profile
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Customer Info */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Customer</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2"><User size={13} className="text-gray-400" /><span className="text-sm text-gray-900 dark:text-white font-medium">{user?.name}</span></div>
            <div className="flex items-center gap-2"><Mail size={13} className="text-gray-400" /><span className="text-sm text-gray-600 dark:text-gray-300">{user?.email}</span></div>
            {user?.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-gray-400" /><span className="text-sm text-gray-600 dark:text-gray-300">{user.phone}</span></div>}
          </div>
        </div>

        {/* ITPL Club Promo — V2 outline-icon redesign */}
        {promoBanner && promoBanner.enabled && (
          <div className="px-4 pt-4" data-testid="itpl-club-promo">
            <div className="relative overflow-hidden rounded-xl border border-amber-200 bg-white dark:bg-gray-900 p-3.5">
              {/* Hairline accent stripe on the left */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-orange-500" />
              <div className="flex items-start gap-3 pl-1">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg border border-emerald-200 bg-emerald-50 flex items-center justify-center">
                  <BadgePercent size={16} strokeWidth={1.75} className="text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {promoBanner.club_label && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.15em] uppercase text-emerald-800">
                        <Sparkles size={9} strokeWidth={2} />
                        {promoBanner.club_label}
                      </span>
                    )}
                    {promoBanner.tagline && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">{promoBanner.tagline}</span>
                      </>
                    )}
                  </div>
                  {promoBanner.headline && (
                    <p className="text-[13px] font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight">
                      {promoBanner.headline}
                    </p>
                  )}
                  {promoBanner.subline && (
                    <p className="text-[10.5px] font-light text-gray-500 mt-0.5 leading-snug">{promoBanner.subline}</p>
                  )}
                  {promoBanner.footer_text && (
                    <div className="mt-1.5 flex items-start gap-1 text-[10px] text-gray-500 font-light leading-relaxed">
                      <MapPin size={10} strokeWidth={1.75} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <span>{promoBanner.footer_text}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Type — V2 stacked + descriptions */}
        <div className="p-4" data-testid="order-type-section">
          <p className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-extrabold mb-2.5">Order Type</p>
          {isPickupMode && (
            <div className="mb-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
              <Store size={12} strokeWidth={1.75} className="text-orange-600" />
              <span className="text-[11px] font-semibold text-orange-700">Pickup Mode Active</span>
            </div>
          )}
          <div className="space-y-2">
            <button
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${!isPickupMode && orderType === "delivery" ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-gray-200 dark:border-gray-700"} ${isPickupMode ? "opacity-40 cursor-not-allowed" : ""}`}
              onClick={() => !isPickupMode && setOrderType("delivery")}
              disabled={isPickupMode}
              data-testid="order-type-delivery"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${!isPickupMode && orderType === "delivery" ? "border-green-300 bg-white" : "border-gray-200 bg-gray-50"}`}>
                <Truck size={16} strokeWidth={1.75} className={!isPickupMode && orderType === "delivery" ? "text-green-700" : "text-gray-500"} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-bold ${!isPickupMode && orderType === "delivery" ? "text-green-800" : "text-gray-900 dark:text-white"}`}>Delivery</p>
                <p className="text-[10px] font-light text-gray-500">Hot meal delivered to your address</p>
              </div>
              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${!isPickupMode && orderType === "delivery" ? "border-green-600 bg-green-600" : "border-gray-300"}`}>
                {!isPickupMode && orderType === "delivery" && <div className="w-full h-full rounded-full bg-white scale-50" />}
              </div>
            </button>
            <button
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${orderType === "pickup" ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-gray-200 dark:border-gray-700"}`}
              onClick={() => setOrderType("pickup")}
              data-testid="order-type-pickup"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${orderType === "pickup" ? "border-green-300 bg-white" : "border-gray-200 bg-gray-50"}`}>
                <Store size={16} strokeWidth={1.75} className={orderType === "pickup" ? "text-green-700" : "text-gray-500"} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-bold ${orderType === "pickup" ? "text-green-800" : "text-gray-900 dark:text-white"}`}>Pick Up</p>
                <p className="text-[10px] font-light text-gray-500">Skip the wait — collect at the kitchen counter</p>
              </div>
              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${orderType === "pickup" ? "border-green-600 bg-green-600" : "border-gray-300"}`}>
                {orderType === "pickup" && <div className="w-full h-full rounded-full bg-white scale-50" />}
              </div>
            </button>
          </div>
        </div>

        {/* Pickup Info or Delivery Address Selection */}
        {orderType === "pickup" ? (
          <div className="p-4 border-b border-gray-100 dark:border-gray-800" data-testid="pickup-info-section">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2"><Store size={10} className="inline mr-1" />Pick Up From Kitchen</p>
            {kitchenInfo ? (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl space-y-1.5">
                <p className="text-sm font-bold text-gray-900 dark:text-white">{kitchenInfo.kitchen_code} — {kitchenInfo.locality ? `${kitchenInfo.locality}, ` : ''}{kitchenInfo.city}</p>
                {kitchenInfo.business_name && <p className="text-xs text-gray-700 dark:text-gray-200 font-medium">{kitchenInfo.business_name}</p>}
                <p className="text-xs text-gray-600 dark:text-gray-300">{kitchenInfo.business_address || kitchenInfo.location || `${kitchenInfo.locality || ''}, ${kitchenInfo.city || ''}`}</p>
                {kitchenInfo.business_phone && (
                  <a href={`tel:${kitchenInfo.business_phone}`} className="flex items-center gap-1.5 text-xs text-green-700 font-semibold">
                    <Phone size={12} className="text-green-600" />{kitchenInfo.business_phone}
                  </a>
                )}
                <div className="flex items-center gap-1 pt-1 border-t border-green-200 dark:border-green-700 mt-1">
                  <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  <span className="text-[10px] text-green-600 font-medium">A delivery code will be shared — present it at pickup</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Kitchen address will be auto-assigned</p>
            )}
          </div>
        ) : forcedAddress ? (
          <div className="p-4 border-b border-gray-100 dark:border-gray-800" data-testid="forced-address-section">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2"><MapPin size={10} className="inline mr-1" />Delivery Address (locked)</p>
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900 dark:text-white">{forcedAddress.segment?.name}</span>
                <span className="text-[9px] px-1.5 py-0.5 bg-emerald-600 text-white rounded font-bold">SEGMENT DEFAULT</span>
              </div>
              <p className="text-xs text-gray-700 dark:text-gray-200">{forcedAddress.display}</p>
              <p className="text-[10px] text-emerald-700 dark:text-emerald-300 font-medium pt-1 border-t border-emerald-200 dark:border-emerald-800 mt-1">
                Your delivery is locked to your segment's default address. Contact your Segment Admin if this needs to change.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 border-b border-gray-100 dark:border-gray-800">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2"><MapPin size={10} className="inline mr-1" />Delivery Address</p>
            {addresses.length === 0 ? (
              <div>
                <p className="text-sm text-gray-500">No saved addresses</p>
                <button onClick={() => navigate("/profile")} className="text-xs text-green-600 font-semibold mt-1">+ Add Address in Profile</button>
              </div>
            ) : selectedAddress ? (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl" data-testid="locked-address">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{selectedAddress.label}</span>
                  <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-bold">SELECTED</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{selectedAddress.address_line}</p>
                <p className="text-[9px] text-gray-500">{[selectedAddress.city, selectedAddress.state, selectedAddress.pincode].filter(Boolean).join(", ")}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500">No address selected</p>
                <button onClick={() => navigate("/menu")} className="text-xs text-green-600 font-semibold mt-1">Select from Menu page</button>
              </div>
            )}
          </div>
        )}

        {/* Cart Items — V2 each item as its own bordered card */}
        <div className="p-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-extrabold mb-2.5">Items</p>
          <div className="space-y-2.5">
            {cart.map((item) => {
              const itemPrice = getItemPrice(item);
              return (
                <div key={item.cart_key} className="flex items-start gap-3 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900" data-testid={`cart-item-${item.cart_key}`}>
                  <img src={item.image_url || "https://images.unsplash.com/photo-1606757819934-d61a9f7279d5?w=100"} alt={item.name} className="w-14 h-14 object-cover rounded-lg flex-shrink-0 border border-gray-100" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[13px] font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight">{item.name}</h3>
                    {item.selected_variations?.length > 0 && (() => {
                      const groups = {};
                      item.selected_variations.forEach(v => {
                        const g = v.variation_name || "Variation";
                        groups[g] = groups[g] || [];
                        groups[g].push(v.option_value);
                      });
                      return Object.entries(groups).map(([g, vals]) => (
                        <p key={g} className="text-[9.5px] font-light text-gray-500">{g}: {vals.join(", ")}</p>
                      ));
                    })()}
                    {item.selected_addons?.length > 0 && (() => {
                      const groups = {};
                      item.selected_addons.forEach(a => {
                        const g = a.group_name || "Add-ons";
                        groups[g] = groups[g] || [];
                        groups[g].push(a.name);
                      });
                      return Object.entries(groups).map(([g, names]) => (
                        <p key={g} className="text-[9.5px] font-light text-gray-500">+ {g}: {names.join(", ")}</p>
                      ));
                    })()}
                    <p className="text-[13px] font-extrabold text-green-700 mt-1 tracking-tight">{symbol}{Number(itemPrice * item.quantity).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="quantity-control">
                      <button className="quantity-btn" onClick={() => updateQuantity(item.cart_key, item.quantity - 1)} data-testid={`cart-minus-${item.cart_key}`}><Minus size={12} strokeWidth={1.75} /></button>
                      <span className="quantity-value">{item.quantity}</span>
                      <button className="quantity-btn" onClick={() => updateQuantity(item.cart_key, item.quantity + 1)} data-testid={`cart-plus-${item.cart_key}`}><Plus size={12} strokeWidth={1.75} /></button>
                    </div>
                    <button onClick={() => removeFromCart(item.cart_key)} className="text-red-400 p-1" data-testid={`cart-remove-${item.cart_key}`}><Trash2 size={13} strokeWidth={1.75} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Instructions — V2 demarcated own card */}
        <div className="px-4 pb-4">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <FileText size={11} strokeWidth={1.75} className="text-gray-500" />
              <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-extrabold">Custom Instructions</p>
            </div>
            <Textarea placeholder="Any special instructions for your order…" value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} rows={2} className="text-[12px] mb-2 bg-white dark:bg-gray-900 border-gray-200 placeholder:font-light" data-testid="order-notes" />
            <Textarea placeholder="Delivery instructions (e.g., ring doorbell, leave at door)…" value={deliveryInstructions} onChange={(e) => setDeliveryInstructions(e.target.value)} rows={1} className="text-[12px] bg-white dark:bg-gray-900 border-gray-200 placeholder:font-light" data-testid="delivery-instructions" />
          </div>
        </div>

        {/* Upsell — V2 bigger cards */}
        {recommendations.length > 0 && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-800" data-testid="cart-recommendations">
            <div className="flex items-center gap-1.5 mb-3">
              <Sparkles size={13} strokeWidth={1.75} className="text-amber-500" />
              <p className="text-[10px] uppercase tracking-[0.18em] text-gray-700 dark:text-gray-200 font-extrabold">You may also like</p>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
              {recommendations.map((item) => (
                <div key={item.item_id} className="flex-shrink-0 w-40 bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-green-300 transition-colors" data-testid={`rec-${item.item_id}`}>
                  <img src={item.image_url || "https://images.unsplash.com/photo-1606757819934-d61a9f7279d5?w=300"} alt={item.name} className="w-full h-24 object-cover" />
                  <div className="p-2.5">
                    <p className="text-[12px] font-bold text-gray-900 dark:text-white line-clamp-1 leading-tight tracking-tight">{item.name}</p>
                    <p className="text-[10px] font-light text-gray-500 line-clamp-1 mt-0.5">{item.description || item.short_description || ""}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[13px] font-extrabold text-gray-900 dark:text-white tracking-tight">{symbol}{Math.round(item.base_price || item.price || 0)}</span>
                      <button onClick={() => { addToCart({ item_id: item.item_id, name: item.name, price: item.base_price || item.price || 0, base_price: item.base_price || item.price || 0, image_url: item.image_url, quantity: 1, selected_variations: [], selected_addons: [], kitchen_id: cart[0]?.kitchen_id, kitchen_code: cart[0]?.kitchen_code }); toast.success(`${item.name} added`); }} className="bg-green-600 hover:bg-green-700 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md flex items-center gap-1" data-testid={`rec-add-${item.item_id}`}>
                        <Plus size={11} strokeWidth={2} /> Add
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!hasBillingAddress && orderType === "delivery" && selectedAddress && (
          <div className="px-4 pb-3 -mt-1 border-b border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-2 cursor-pointer bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2.5" data-testid="billing-same-as-shipping">
              <input
                type="checkbox"
                checked={billingMatchesShipping}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  setBillingMatchesShipping(checked);
                  if (checked && selectedAddress) {
                    try {
                      const res = await fetch(`${API}/billing-address`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                          address_line: selectedAddress.address_line,
                          landmark: selectedAddress.landmark || "",
                          city: selectedAddress.city || "",
                          state: selectedAddress.state || "",
                          pincode: selectedAddress.pincode || "",
                        }),
                      });
                      if (res.ok) {
                        setHasBillingAddress(true);
                        toast.success("Billing address set to delivery address");
                      }
                    } catch { toast.error("Failed to update billing address"); }
                  }
                }}
                className="w-4 h-4 rounded border-green-400 text-green-600 focus:ring-green-500"
              />
              <span className="text-xs font-medium text-green-700 dark:text-green-400">Use delivery address as billing address</span>
            </label>
          </div>
        )}

        {!hasBillingAddress && !(orderType === "delivery" && selectedAddress) && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800/30">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-700">Billing Address Required</p>
                <p className="text-xs text-amber-600">Add a billing address in your profile to place orders</p>
                <Button onClick={() => navigate("/profile")} size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="add-billing-btn">
                  Add Billing Address
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Method Selection */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800" data-testid="payment-method-section">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">Payment Method</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${paymentMethod === "wallet" ? "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700" : "border-gray-200 dark:border-gray-700 text-gray-500"}`}
              onClick={() => setPaymentMethod("wallet")}
              data-testid="payment-wallet"
            >
              <Wallet size={20} />
              <span className="text-xs font-semibold">Wallet</span>
              <span className="text-[9px] text-gray-500">{symbol}{Math.round(user?.wallet_balance || 0)}</span>
            </button>
            <button
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${paymentMethod === "gateway" ? "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700" : "border-gray-200 dark:border-gray-700 text-gray-500"}`}
              onClick={() => setPaymentMethod("gateway")}
              data-testid="payment-gateway"
            >
              <CreditCard size={20} />
              <span className="text-xs font-semibold">Pay Online</span>
              <span className="text-[9px] text-gray-500">UPI / Card / Net Banking</span>
            </button>
          </div>
          {/* Discount eligibility message */}
          {discountConfig?.enabled && (
            <div className="mt-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-[10px] text-yellow-700 font-medium">
                {discountConfig.applicable_to === "both" 
                  ? `${discountConfig.label}: ${discountConfig.type === "percentage" ? `${discountConfig.value}% off` : `${symbol}${discountConfig.value} off`} on all orders`
                  : discountConfig.applicable_to === paymentMethod
                    ? `${discountConfig.label} applied: ${discountConfig.type === "percentage" ? `${discountConfig.value}%` : `${symbol}${discountConfig.value}`} off!`
                    : `Switch to ${discountConfig.applicable_to === "wallet" ? "Wallet" : "Pay Online"} to get ${discountConfig.type === "percentage" ? `${discountConfig.value}%` : `${symbol}${discountConfig.value}`} off`}
              </p>
            </div>
          )}
        </div>

        {paymentMethod === "wallet" && hasInsufficientBalance && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800/30">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-600">Insufficient Balance</p>
                <p className="text-xs text-red-500">You need {symbol}{Number(total - Math.round(user.wallet_balance)).toFixed(2)} more to place this order</p>
                <Button onClick={() => navigate("/wallet")} size="sm" className="mt-2 bg-green-600 hover:bg-green-700 text-white text-xs" data-testid="recharge-btn">
                  Load Credits Now
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Minimum Order Value warning */}
        {!movSatisfied && minOrderValue > 0 && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800/30" data-testid="mov-warning">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-700">
                  Minimum {orderType === "pickup" ? "pickup" : "delivery"} order value: {symbol}{Number(minOrderValue).toFixed(0)}
                </p>
                <p className="text-xs text-amber-600">
                  You need {symbol}{Number(minOrderValue - (subtotal - discountAmount)).toFixed(2)} more (after discount) to place this order. Please add more items.
                </p>
                <Button onClick={() => navigate("/menu")} size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="mov-add-more-btn">
                  Add More Items
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Spacer for fixed bottom bar */}
        <div className="h-64" />

        {/* Fixed Bottom */}
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="px-4 pt-2.5 pb-1">
            {/* Bill summary header — tap to expand breakdown */}
            <button
              type="button"
              onClick={() => setBillExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 py-1 group"
              data-testid="bill-summary-toggle"
            >
              <span className="text-[9.5px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center gap-1">
                Bill Summary
                {billExpanded
                  ? <ChevronUp size={11} strokeWidth={2} className="text-gray-400" />
                  : <ChevronDown size={11} strokeWidth={2} className="text-gray-400" />}
              </span>
              <span className="text-[10px] font-light text-gray-400">
                {previewLoading ? "updating…" : "tap to view"}
              </span>
            </button>
            {billExpanded && (
              <div className="space-y-0.5 mt-1 mb-1.5 pl-2 border-l-2 border-gray-100 dark:border-gray-700">
                <div className="flex justify-between text-[11px]"><span className="text-gray-500 font-light">Base Amount</span><span className="text-gray-700">{symbol}{Number(subtotal).toFixed(2)}</span></div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-[11px]" data-testid="cart-discount-row"><span className="text-green-600 font-medium">{discountLabel}</span><span className="text-green-600 font-semibold">-{symbol}{Number(discountAmount).toFixed(2)}</span></div>
                )}
                {packagingCharge > 0 && (
                  <div className="flex justify-between text-[11px]" data-testid="cart-packaging-row"><span className="text-gray-500 font-light">Packaging &amp; Handling</span><span className="text-gray-700">{symbol}{Number(packagingCharge).toFixed(2)}</span></div>
                )}
                {taxBreakdown.length > 0 ? (
                  taxBreakdown.map((tx) => (
                    tx.amount > 0 && (
                      <div key={`${tx.name}-${tx.percentage}`} className="flex justify-between text-[11px]" data-testid={`cart-tax-${tx.name}`}>
                        <span className="text-gray-500 font-light">{tx.name} {tx.percentage}%</span>
                        <span className="text-gray-700">{symbol}{Number(tx.amount).toFixed(2)}</span>
                      </div>
                    )
                  ))
                ) : (
                  taxAmount > 0 && (
                    <div className="flex justify-between text-[11px]" data-testid="cart-tax-row"><span className="text-gray-500 font-light">Tax</span><span className="text-gray-700">{symbol}{Number(taxAmount).toFixed(2)}</span></div>
                  )
                )}
                {Math.abs(roundingAdjustment) >= 0.01 && (
                  <div className="flex justify-between text-[10px]" data-testid="cart-rounding-row"><span className="text-gray-400 font-light">Round Off</span><span className="text-gray-500">{roundingAdjustment > 0 ? "+" : "-"}{symbol}{Math.abs(roundingAdjustment).toFixed(2)}</span></div>
                )}
              </div>
            )}
            {/* Always-visible total row */}
            <div className="flex justify-between items-baseline pt-1.5 border-t border-gray-200 dark:border-gray-700">
              <span className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-700 dark:text-gray-200">Total</span>
              <span className="text-lg font-extrabold text-green-600 tracking-tight" data-testid="cart-total">{symbol}{Number(total).toFixed(2)}</span>
            </div>
            {paymentMethod === "wallet" && (
              <div className="flex justify-between text-[10px] mt-0.5">
                <span className="text-gray-400 font-light">Wallet</span>
                <span className={`font-semibold ${hasInsufficientBalance ? 'text-red-500' : 'text-green-600'}`}>{symbol}{Number(user?.wallet_balance || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
          <button
            className="w-full py-4 text-sm font-bold uppercase tracking-wide bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            onClick={handlePlaceOrder}
            disabled={loading || gatewayLoading || (paymentMethod === "wallet" && hasInsufficientBalance) || !hasBillingAddress || (orderType === "delivery" && !selectedAddressId) || !movSatisfied}
            data-testid="place-order-btn"
          >
            {loading || gatewayLoading
              ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{gatewayLoading ? "Processing..." : "Placing..."}</span>
              : !hasBillingAddress
                ? "Billing Address Required"
                : !movSatisfied
                  ? `Min Order ${symbol}${Number(minOrderValue).toFixed(0)}`
                  : paymentMethod === "gateway"
                  ? `Pay ${symbol}${Number(total).toFixed(2)} & Place Order`
                  : orderType === "pickup"
                    ? "Place Pickup Order"
                    : "Place Order"
            }
          </button>
        </div>
      </div>
    </UserLayout>
  );
}
